"use client";

import { useSyncExternalStore, useCallback, useRef } from "react";
import { shallowEqual } from "./shallowEqual";

export interface SelectorStoreConfig<TState> {
  getSnapshot: () => TState;
  subscribe: (listener: () => void) => () => void;
  defaultState: TState;
}

/**
 * Generic selector hook for external stores with:
 * - Shallow comparison for memoization
 * - Hydration-safe SSR support
 * - Cached value for referential stability
 *
 * This hook is used by both useAudioEngineState and useAudioEngineTicker
 * to share the complex subscription logic while allowing different stores.
 *
 * @param config - Store configuration (getSnapshot, subscribe, defaultState)
 * @param selector - Function to select/derive state from the full store state
 * @param hydrated - Whether the app has hydrated (false during SSR)
 */
export function useEngineSelector<TState, TSelected>(
  config: SelectorStoreConfig<TState>,
  selector: (state: TState) => TSelected,
  hydrated: boolean
): TSelected {
  // Keep track of previous selected value for comparison
  const cachedValueRef = useRef<TSelected | null>(null);

  // This is an intentional override of the eslint rule, as this is finely tuned code
  /* eslint-disable react-hooks/refs */

  // Must update synchronously for useSyncExternalStore getSnapshot to use latest selector
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  // Must update synchronously for useSyncExternalStore getSnapshot to use latest hydration state
  const hydratedRef = useRef(hydrated);
  hydratedRef.current = hydrated;

  /* eslint-enable react-hooks/refs */

  // getSnapshot returns the current selected value, using cache for referential stability
  const getSnapshot = useCallback((): TSelected => {
    const state = hydratedRef.current ? config.getSnapshot() : config.defaultState;
    const newValue = selectorRef.current(state);

    // Return cached value if shallow-equal (referential stability)
    if (cachedValueRef.current !== null && shallowEqual(cachedValueRef.current, newValue)) {
      return cachedValueRef.current;
    }

    cachedValueRef.current = newValue;
    return newValue;
  }, [config]);

  // Subscribe only notifies React when the selected value actually changes.
  // This is critical for performance - without this, every subscriber would
  // be notified 60x/sec during playback even if their selected value hasn't changed.
  //
  // IMPORTANT: We only compare here, we do NOT update cachedValueRef.
  // Only getSnapshot updates the cache to avoid race conditions between
  // subscribe callbacks and React calling getSnapshot.
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      return config.subscribe(() => {
        const state = hydratedRef.current ? config.getSnapshot() : config.defaultState;
        const newValue = selectorRef.current(state);

        // Only notify React if the selected value has changed
        // Let getSnapshot handle updating the cache
        if (cachedValueRef.current === null || !shallowEqual(cachedValueRef.current, newValue)) {
          onStoreChange();
        }
      });
    },
    [config]
  );

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
